/**
 * @fileoverview The Weather page's frost card: each season's frost (36°F) with its spread,
 * and under it the freeze (32°F), in the app's cold colours.
 *
 * Each season can be linked to, /weather#frost-fall and #frost-spring (the
 * card as a whole is #frost), which is where Today's frost countdown and
 * its frost and freeze dates lead. Arriving by one scrolls it into view and
 * rings it.
 *
 * Where: the foot of the Weather page; reached by /weather#frost,
 * #frost-fall and #frost-spring from Today's frost countdown.
 * API: GET /weather/frost-outlook?zip= (getFrostOutlook), for the garden's
 * postcode as the server says it (useGardenPostcode, the garden place: the
 * owner's in a shared garden, where the server reads the owner's whatever
 * is sent; audit A29). "Days away" count from the garden's own day, the
 * server's.
 * Settings: FROST_ARRIVAL_FOLLOW_MS and TODAY_QUERY_RETRIES (config/tunables/today);
 * QUERY_STALE_TIME_MS. The temperatures the dates are for (36°F and 32°F) are
 * the server's (thresholds: the planner's, fixed by NOAA's columns).
 * Words (en/es): the season names, dates, range, status line (B144: fall's line once its usual window has passed with no frost,
 * drawn under the dates when the server sends it), title, paragraph and
 * disclaimer are the server's (its frostCard.* words, in the gardener's
 * language: the printed planner's strings in English); so are the frost and
 * freeze lines' colours (tokens). The app's own words: weather.frostLine,
 * weather.frostWhat, weather.freezeLine,
 * weather.freezeWhat, weather.frostExplain, weather.hardyNote,
 * weather.station, weather.stationData.
 * Watch out: it draws nothing at all without a postcode Grow can plan from
 * or a successful answer, on purpose: the rest of the page still stands.
 *
 * @module FrostOutlook
 */

import { retryRule } from '@/config/queryRetry';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { conditionTextClass } from '@/shared/utils/outlookSeverity';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { getFrostOutlook } from '@/shared/api/frostOutlook';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { FROST_ARRIVAL_FOLLOW_MS, TODAY_QUERY_RETRIES } from '@/config/tunables/today';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { formatTempWithTranslation } from '@/shared/utils/temperatureUtils';

/**
 * When frost comes, and how firm those dates are.
 *
 * Every word here is the server's, taken from the printed planner's own string
 * table, and so are the dates. Grow had started to grow a second frost story
 * — its own station lookup, its own wording for what a probability means — and
 * a second story about frost is worse than none, because the two only have to
 * disagree once to cost somebody their seedlings.
 *
 * Both seasons are shown rather than whichever is next. That is what the
 * planner does, and it removes the question of which frost the reader meant.
 *
 * The dates are what somebody came for; the prose explaining what a typical
 * date means, where the numbers came from, and what they do not promise is
 * worth reading once and then never again. It is folded away by default so the
 * two dates are the whole of what this section says until asked.
 */
export const FrostOutlook: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { hash } = useLocation();
  const { userConfig } = useUserConfig();
  const unit = userConfig.preferences.temperatureUnit;
  const [isExplained, setIsExplained] = React.useState(false);
  // The garden's postcode, and whether it can be planned, as the server says.
  const { zip, usable } = useGardenPostcode();

  const { data } = useQuery({
    queryKey: ['frost-outlook', zip],
    queryFn: () => getFrostOutlook(zip),
    enabled: usable && zip.length > 0,
    staleTime: QUERY_STALE_TIME_MS,
    retry: retryRule(TODAY_QUERY_RETRIES),
  });

  /*
   * Arrived by a link to the frost dates: brought into view once they are
   * drawn. The page above is often still filling in (the garden's week
   * arrives after these dates) and pushes the card down, so it is followed
   * as the page grows, until the gardener scrolls or taps, or for a few
   * seconds. Not again when the dates are read afresh: only a new link, or
   * the dates arriving, starts it.
   */
  const target = hash.startsWith('#frost') ? hash.slice(1) : null;
  React.useEffect(() => {
    if (!target || !data?.success || typeof ResizeObserver === 'undefined') return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let first = true;
    /** Brings the card to the middle of the screen: gently the first time, at once after. */
    const bring = (): void => {
      document.getElementById(target)?.scrollIntoView({ behavior: first && !reduce ? 'smooth' : 'auto', block: 'center' });
      first = false;
    };
    const observer = new ResizeObserver(bring);
    const inputs = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    /** Stops following: the gardener has taken over, or the page has settled. */
    const stop = (): void => {
      observer.disconnect();
      inputs.forEach((name) => window.removeEventListener(name, stop));
    };
    observer.observe(document.body);
    inputs.forEach((name) => window.addEventListener(name, stop, { passive: true }));
    const timer = window.setTimeout(stop, FROST_ARRIVAL_FOLLOW_MS);
    return () => { stop(); window.clearTimeout(timer); };
  }, [target, data?.success]);

  // No postal code, or nowhere to draw the dates from. The page below is still
  // worth reading, so this simply says nothing.
  if (!data?.success) return null;

  /*
   * Autumn frost ends a season, spring frost releases one, and they are read
   * for opposite reasons — so they are not two rows of the same colour. Amber
   * for the one that closes the year, green for the one that opens it.
   *
   * The server puts the nearer season first, because in September the spring
   * date is seven months of gardening away and does not belong at the top.
   */
  // The frost (36°F) and freeze (32°F) the server says the dates are for, in the gardener's own unit, not the
  // language's.
  const frostTemp = formatTempWithTranslation(data.thresholds.frostF, unit, t);
  const freezeTemp = formatTempWithTranslation(data.thresholds.freezeF, unit, t);

  /** Each season's card, date and label colours. */
  const tone = {
    fall: {
      card: 'border-warning/40 bg-warning-tint',
      date: 'text-warning',
      label: 'text-warning',
    },
    spring: {
      card: 'border-verdict-good/40 bg-verdict-good-tint',
      date: 'text-verdict-good',
      label: 'text-verdict-good',
    },
  } as const;

  return (
    <section id="frost" className="scroll-mt-20">
      <h2 className="mb-2 text-sm font-medium">{data.copy.title}</h2>

      <div className="rounded-lg border border-border bg-card p-4">
        {data.seasons.length > 0 && (
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.seasons.map((season) => (
              <li
                key={season.key}
                id={`frost-${season.key}`}
                className={cn(
                  'rounded-md border p-3',
                  tone[season.key].card,
                  // The season a link led here for.
                  target === `frost-${season.key}` && 'ring-2 ring-verdict-cool ring-offset-2 ring-offset-background',
                )}
              >
                <div className={cn('text-xs font-medium', tone[season.key].label)}>
                  {season.label}
                </div>
                <div className="mt-0.5 flex items-baseline gap-1.5">
                  <span className={cn('text-lg font-semibold', tone[season.key].date)}>
                    {season.typical}
                  </span>
                  <span className="text-xs text-muted-foreground">{data.copy.typicalNote}</span>
                </div>
                {/* The spread, in the planner's words. A typical date on its own
                    is the most confidently wrong number in gardening. */}
                <p className="mt-1 text-xs leading-snug text-muted-foreground">{season.range}</p>
                {/* Where the season stands once its usual window has passed (B144): the server's words, drawn as sent.
                    Null in every other case; the dates above stay all year. */}
                {season.status && <p className="mt-1 text-xs font-medium leading-snug text-muted-foreground">{season.status.text}</p>}
                {/* The two readings, in the colours the server sends for them
                    (tokens: cool for worth knowing, cold for time to act). */}
                <p className="mt-2 text-xs leading-snug">
                  <span className={cn('font-medium', conditionTextClass(data.tokens.frost))}>{t('weather.frostLine', { temp: frostTemp })}</span>
                  <span className="text-muted-foreground"> · {t('weather.frostWhat')}</span>
                </p>
                {season.freeze && (
                  <p className="mt-1 text-xs leading-snug">
                    <span className={cn('font-medium', conditionTextClass(data.tokens.freeze))}>{t('weather.freezeLine', { temp: freezeTemp })} {season.freeze.typical}</span>
                    <span className="text-muted-foreground"> · {t('weather.freezeWhat')}</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {/*
          * The reading, folded away.
          *
          * The label does not change when it opens, so nothing on the row
          * moves and the chevron carries the state — the same control the
          * plant card uses, for the same reason.
          */}
        <button
          type="button"
          onClick={() => setIsExplained((open) => !open)}
          aria-expanded={isExplained}
          className="mt-3 flex w-full items-center justify-between gap-2 rounded-sm text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('weather.frostExplain')}
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 transition-transform', isExplained && 'rotate-180')}
            aria-hidden="true"
          />
        </button>

        {isExplained && (
          <div className="mt-2">
            <p className="text-xs leading-relaxed text-muted-foreground">
              {data.copy.paragraph}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t('weather.hardyNote')}</p>

            {data.station.url && (
              <p className="mt-1 text-xs text-muted-foreground">
                {/* Said for what it is: the name alone read as a stray word. */}
                {t('weather.station')}{' '}
                <a
                  href={data.station.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {data.station.name}
                </a>
                {' '}{t('weather.stationData')}
              </p>
            )}

            {data.copy.disclaimer && (
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {data.copy.disclaimer}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
